// src/utils/audienceView.js
// Creator Metrics audience view (AUDIENCE_METRICS_DESIGN.md 2.5.2; release
// R4 = AM-2).
//
// One pure function turns a getMyAudienceMetrics payload (v1, design 2.4.5)
// into every display string of the Audience card, the "Who watched live"
// block in the Streaming card and the Videos card, exactly as written in the
// design's section 5.1 (the founder-approved copy). The server enforces the
// privacy floors and sends null for anything suppressed; this file only
// picks which approved line a payload earns, so the harness can test the
// copy exactly.
//
// Member rows (the members cell, the tier line, member lines and member or
// tier notes) show only once memberships have ever launched or the creator
// already has a member (the earningsLanes.js rule), so the dark screen shows
// none of them.
//
// A missing or invalid payload, or one for a different month than the
// screen shows, gives null: the screen then renders none of the new rows.
//
// Zero imports and ASCII-only source: unit-tested in plain node
// (web-landing/scripts/test-audience-view.mjs).

const DOT = ' \u00b7 ';
// The copy's "5" (the server's AUDIENCE_SPLIT_FLOOR). The server decides
// every split; this only picks which note explains a hidden one.
const SPLIT_FLOOR = 5;

// Ladder order and fallback names. The payload's members.tiers[].name wins;
// the tiers harness pins this list to constants/membershipTiers.js TIER_KEYS
// (STEP3_5A_TIERS_DESIGN.md 7.1.1).
export const TIERS = [['spark', 'Spark'], ['amped', 'Amped'], ['overdrive', 'Overdrive'], ['maxed', 'Maxed']];

// The Streaming card's renamed cells (were "stream viewers" and "watched by
// others"). Shown whether or not the audience payload loaded.
export const LIVE_VIEWERS_LABEL = 'live viewers';
export const LIVE_WATCH_LABEL = 'live watch time';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MK_RE = /^(\d{4})-(\d{2})$/;
const BASES = ['none', 'first_watch', 'today', 'mixed'];
const RECORDED = ['none', 'partial', 'full'];

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

// "1,234": the screen's fmtNum for whole counts.
export function fmtCount(n) {
  return Math.round(Number(n) || 0).toLocaleString('en-US');
}

// "45m" / "13h 33m": the screen's fmtMinutes.
export function fmtWatch(mins) {
  const m = Math.round(Number(mins) || 0);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

// "Oct 16, 2026": the screen's fmtDate format, in UTC like {Month} (the
// recording start is the same date for every viewer, wherever they are).
export function fmtDay(ms) {
  return new Date(Number(ms) || 0).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

const utcMonthKey = (ms) => new Date(ms).toISOString().slice(0, 7);

// {Month}: the long UTC month name, with the year when it is not the current
// UTC year ("October", "December 2025"). null for a malformed key.
export function monthNameOf(monthKey, nowMs) {
  const mk = MK_RE.exec(String(monthKey || ''));
  if (!mk) return null;
  const idx = Number(mk[2]) - 1;
  if (idx < 0 || idx > 11) return null;
  const year = Number(mk[1]);
  const now = isNum(nowMs) ? nowMs : Date.now();
  return year === new Date(now).getUTCFullYear() ? MONTH_NAMES[idx] : `${MONTH_NAMES[idx]} ${year}`;
}

// The Streaming card's sign-in hint (getMyCreatorMetrics' streaming.anonViewers,
// so it shows even when the audience payload did not load). null at 0.
export function streamSignedOutHint(anonViewers) {
  const n = Math.round(Number(anonViewers) || 0);
  if (!(n > 0)) return null;
  return {
    strong: `${fmtCount(n)} signed-out visit${n === 1 ? '' : 's'}`,
    rest: ' to your streams. Signed-in viewers can chat, follow you, and send Super Crits and Rumbles, so invite your audience to sign up.',
  };
}

// Tier display names: the payload's, else the fallback list.
function tierNamesOf(members) {
  const names = {};
  TIERS.forEach(([k, n]) => { names[k] = n; });
  const list = members && Array.isArray(members.tiers) ? members.tiers : [];
  list.forEach((t) => {
    if (isObj(t) && Object.prototype.hasOwnProperty.call(names, t.key) && typeof t.name === 'string' && t.name.trim()) {
      names[t.key] = t.name.trim();
    }
  });
  return names;
}

// members.tiers in ladder order, known keys only.
function listedTiersOf(members, names) {
  const list = members && Array.isArray(members.tiers) ? members.tiers : [];
  const out = [];
  TIERS.forEach(([k]) => {
    const t = list.find((x) => isObj(x) && x.key === k);
    if (t) out.push({ key: k, name: names[k], now: isNum(t.now) ? t.now : 0 });
  });
  return out;
}

// " (Spark 2 [dot] Amped 1)": the listed tiers plus any tier with a count, in
// ladder order, so the parts always add up to the member total.
function tierParen(byTier, listedKeys, names) {
  if (!isObj(byTier)) return '';
  const parts = [];
  TIERS.forEach(([k]) => {
    if (listedKeys.includes(k) || (isNum(byTier[k]) && byTier[k] > 0)) {
      parts.push(`${names[k]} ${fmtCount(byTier[k])}`);
    }
  });
  return parts.length ? ` (${parts.join(DOT)})` : '';
}

// payload: the getMyAudienceMetrics result (or null before it loads / after a
// failure). options: { membershipsEverLaunched, monthKey, nowMs }.
export function deriveAudienceView(payload, options) {
  const opts = options || {};
  if (!isObj(payload) || payload.v !== 1) return null;
  const nowMs = isNum(opts.nowMs) ? opts.nowMs : Date.now();
  const monthKey = MK_RE.test(String(opts.monthKey || '')) ? opts.monthKey : payload.monthKey;
  // A payload for another month (a stale response) is not this view's.
  if (payload.monthKey !== monthKey) return null;
  const month = monthNameOf(monthKey, nowMs);
  if (!month) return null;
  const isCurrentMonth = monthKey === utcMonthKey(nowMs);
  const dateText = isNum(payload.recordedFromMs) && payload.recordedFromMs > 0
    ? fmtDay(payload.recordedFromMs)
    : null;

  const splits = isObj(payload.splits) ? payload.splits : {};
  const splitFollowers = splits.followers === true;
  const splitMembers = splits.members === true;
  // The server never sends a tier breakdown for a past month; never show one.
  const splitTiers = splits.tiers === true && isCurrentMonth;

  const followers = isObj(payload.followers) ? payload.followers : {};
  const members = isObj(payload.members) ? payload.members : null;
  const membersNow = members && isNum(members.now) ? members.now : null;
  const memberRows = membersNow !== null && (!!opts.membershipsEverLaunched || membersNow > 0);
  const inMonth = members && isNum(members.inMonth) ? members.inMonth : null;
  const allTiered = !!members && members.allTiered === true;
  const names = tierNamesOf(members);
  const listed = members ? listedTiersOf(members, names) : [];
  const listedKeys = listed.map((t) => t.key);

  const followerNote = 'The follower split shows once you have 5 followers.';
  // The member floor note (members now, then members in the month).
  const memberFloorNote = () => {
    if (!memberRows) return null;
    if (membersNow < SPLIT_FLOOR) return 'Member viewing shows once you have 5 members.';
    if (inMonth !== null && inMonth < SPLIT_FLOOR) {
      return `Member viewing for ${month} is hidden because you had fewer than 5 members then.`;
    }
    return null;
  };
  // The tier note, only under a member line it explains. The current-month
  // note shows only when a tier really has 1 to 4 members now, so it never
  // names a reason that is not the one hiding the breakdown.
  const smallTierNow = listed.some((t) => t.now >= 1 && t.now < SPLIT_FLOOR);
  const tierNote = (memberLineShown) => {
    if (!memberLineShown || !splitMembers || !allTiered) return null;
    if (!isCurrentMonth) return 'The breakdown by tier shows for the current month only.';
    if (!splitTiers && smallTierNow) return 'The breakdown by tier shows when no tier has 1 to 4 members.';
    return null;
  };
  const memberLineOf = (n, byTier) => `Members who watched: ${fmtCount(n)}${splitTiers ? tierParen(byTier, listedKeys, names) : ''}`;

  // ------------------------------------------------------------ Audience card
  const cells = [];
  if (isNum(followers.now)) cells.push({ value: fmtCount(followers.now), label: 'followers' });
  const newIn = isNum(followers.newInMonth) ? followers.newInMonth : null;
  if (newIn !== null) cells.push({ value: newIn > 0 ? `+${fmtCount(newIn)}` : '0', label: 'new followers' });
  if (memberRows) cells.push({ value: fmtCount(membersNow), label: 'members' });
  const audience = cells.length > 0 ? {
    title: 'Audience',
    cells,
    tierLine: memberRows && allTiered && listed.length > 0
      ? `Members by tier: ${listed.map((t) => `${t.name} ${fmtCount(t.now)}`).join(DOT)}`
      : null,
    footnote: 'Followers and members are counts for right now.'
      + (newIn !== null ? ` New followers are accounts that followed you for the first time in ${month}.` : ''),
  } : null;

  // ------------------------------------------------------------ Live
  const live = isObj(payload.live) ? payload.live : {};
  const lv = isObj(live.viewers) ? live.viewers : null;
  const lm = isObj(live.minutes) ? live.minutes : null;
  const viewersOk = !!lv && isNum(lv.total) && isNum(lv.signedIn) && isNum(lv.signedOut);
  const minutesOk = !!lm && isNum(lm.total) && isNum(lm.signedIn) && isNum(lm.signedOut);
  const basis = BASES.includes(live.basis) ? live.basis : null;

  let block = null;
  if (viewersOk && lv.total > 0) {
    const vSplit = splitFollowers && isNum(lv.followers) && isNum(lv.notFollowing);
    const viewersLine = vSplit
      ? `Followers ${fmtCount(lv.followers)}${DOT}Signed in, not following ${fmtCount(lv.notFollowing)}${DOT}Signed-out visits ${fmtCount(lv.signedOut)}`
      : `Signed in ${fmtCount(lv.signedIn)}${DOT}Signed-out visits ${fmtCount(lv.signedOut)}`;
    let watchLine = null;
    if (minutesOk) {
      // Whole minutes, rounded cumulatively so the parts always add up to the
      // "live watch time" cell (fmtWatch(lm.total)); rounding each part on its
      // own could show 1m + 1m under a 1m total.
      const rTotal = Math.round(lm.total);
      const rIn = Math.min(rTotal, Math.round(lm.signedIn));
      const rOut = rTotal - rIn;
      const mSplit = splitFollowers && isNum(lm.followers) && isNum(lm.notFollowing);
      if (mSplit) {
        const rF = Math.min(rIn, Math.round(lm.followers));
        watchLine = `Watch time: followers ${fmtWatch(rF)}${DOT}signed in, not following ${fmtWatch(rIn - rF)}${DOT}signed out ${fmtWatch(rOut)}`;
      } else {
        watchLine = `Watch time: signed in ${fmtWatch(rIn)}${DOT}signed out ${fmtWatch(rOut)}`;
      }
    }
    const memberShown = memberRows && splitMembers && !!basis && basis !== 'today' && isNum(lv.members);
    const memberLine = memberShown ? memberLineOf(lv.members, lv.byTier) : null;
    const memberWatchLine = memberShown && minutesOk && isNum(lm.members)
      ? `Member watch time: ${fmtWatch(lm.members)}`
      : null;

    const notes = [];
    if (memberRows && basis === 'today') {
      if (dateText) notes.push(`Member viewing is counted from ${dateText}.`);
    } else {
      const n = memberFloorNote();
      if (n) notes.push(n);
    }
    const tn = tierNote(memberShown);
    if (tn) notes.push(tn);
    if (!splitFollowers) notes.push(followerNote);

    let footnote = null;
    if (basis === 'first_watch') {
      footnote = `Each signed-in account counts once in ${month}, and each signed-out visit counts once. Followers and members are counted as they were when each viewer first watched you in ${month}. Your own views never count.`;
    } else if (basis === 'mixed' && dateText) {
      footnote = `Each signed-in account counts once in ${month}, and each signed-out visit counts once. Followers and members are counted as they were when each viewer first watched you in ${month}. For viewers who first watched before ${dateText}, followers are counted as they are today and members are not counted. Your own views never count.`;
    } else if (basis === 'today') {
      footnote = `Each signed-in account counts once in ${month}, and each signed-out visit counts once. Followers are counted as they are today. Your own views never count.`;
    } else if (basis === 'none') {
      footnote = 'Each signed-out visit counts once. Your own views never count.';
    }

    block = {
      title: 'Who watched live',
      viewersLine,
      watchLine,
      memberLine,
      memberWatchLine,
      notes,
      footnote,
      capNote: live.capped === true
        ? 'Counts stop at 1,000 signed-in accounts and 1,000 signed-out visits in a month.'
        : null,
    };
  }

  // ------------------------------------------------------------ Videos card
  let videos = null;
  const vid = isObj(payload.videos) ? payload.videos : null;
  if (vid && RECORDED.includes(vid.recorded)) {
    if (vid.recorded === 'none') {
      videos = dateText ? {
        title: 'Videos',
        countedFrom: `Video audience is counted from ${dateText}.`,
        rows: [],
        notes: [],
        footnote: null,
        capNote: null,
      } : null;
    } else {
      const rows = [];
      [['stream', 'Stream videos'], ['upload', 'Uploads']].forEach(([key, title]) => {
        const r = isObj(vid[key]) ? vid[key] : null;
        if (!r || !isNum(r.total) || !isNum(r.signedIn) || !isNum(r.signedOut)) return;
        if (r.total > 0) {
          const split = splitFollowers && isNum(r.followers) && isNum(r.notFollowing);
          const line = split
            ? `Viewers ${fmtCount(r.total)}: followers ${fmtCount(r.followers)}${DOT}signed in, not following ${fmtCount(r.notFollowing)}${DOT}signed-out visits ${fmtCount(r.signedOut)}`
            : `Viewers ${fmtCount(r.total)}: signed in ${fmtCount(r.signedIn)}${DOT}signed-out visits ${fmtCount(r.signedOut)}`;
          const memberLine = memberRows && splitMembers && isNum(r.members) ? memberLineOf(r.members, r.byTier) : null;
          rows.push({ key, title, line, memberLine, hasViews: true });
        } else {
          rows.push({
            key,
            title,
            line: isCurrentMonth ? `No views in ${month} yet.` : `No views in ${month}.`,
            memberLine: null,
            hasViews: false,
          });
        }
      });
      if (rows.length > 0) {
        const anyViews = rows.some((r) => r.hasViews);
        const notes = [];
        if (anyViews) {
          const n = memberFloorNote();
          if (n) notes.push(n);
        }
        const tn = tierNote(rows.some((r) => r.memberLine !== null));
        if (tn) notes.push(tn);
        if (anyViews && !splitFollowers) notes.push(followerNote);
        videos = {
          title: 'Videos',
          countedFrom: null,
          rows,
          notes,
          footnote: `Each signed-in account counts once in ${month} in each row, and each signed-out visit to a video counts once. Followers and members are counted as they were when each viewer first watched one of your videos in ${month}. Your own views never count.`
            + (vid.recorded === 'partial' && dateText ? ` Video audience is counted from ${dateText}.` : ''),
          capNote: vid.capped === true ? 'Counts stop at 1,000 signed-in accounts in a month.' : null,
        };
      }
    }
  }

  return {
    monthKey,
    month,
    isCurrentMonth,
    memberRows,
    partial: payload.partial === true,
    audience,
    live: {
      viewersLabel: LIVE_VIEWERS_LABEL,
      watchLabel: LIVE_WATCH_LABEL,
      // The Streaming card's cells read these when present, so the split
      // below always adds up to the totals above it.
      total: viewersOk ? lv.total : null,
      totalText: viewersOk ? fmtCount(lv.total) : null,
      minutes: minutesOk ? lm.total : null,
      minutesText: minutesOk ? fmtWatch(lm.total) : null,
      block,
    },
    videos,
  };
}
